import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  notificationsApi,
  type NotificationListParams,
  type NotificationListResponse,
} from '../services/notifications'

import { queryKeys } from './api-keys'
import { useSignalR } from './useSignalR'

/**
 * Live-updating list of notifications for the current user.
 *
 * - Fetches from `/api/notifications` via TanStack Query.
 * - Subscribes to the `NotificationCreated` SignalR event and invalidates
 *   the cache so the bell + activity feed refresh without polling.
 *
 * Pass `take: N` to limit the list size — Dashboard's Activity Feed uses
 * `take: 10` while the bell drawer uses `take: 30`.
 */
export function useNotifications(params: NotificationListParams = {}) {
  const qc = useQueryClient()

  useSignalR('NotificationCreated', () => {
    // The pushed payload IS a NotificationDto, but we don't need to use it —
    // simpler to refetch a small list than maintain optimistic state across
    // multiple subscribers. The unread-count query is invalidated too so the
    // badge updates instantly.
    qc.invalidateQueries({ queryKey: queryKeys.notifications.all })
  })

  return useQuery<NotificationListResponse>({
    queryKey: queryKeys.notifications.list({ ...params }),
    queryFn: ({ signal }) =>
      notificationsApi.list(params).then((r) => {
        // `signal` is honored by axios automatically via api.ts wiring.
        void signal
        return r
      }),
    staleTime: 30_000,
  })
}

/**
 * Just the unread counter — used by the bell badge. Kept as a separate query
 * so the badge can stay snappy even while the full list is being refetched.
 */
export function useUnreadNotificationCount() {
  const qc = useQueryClient()

  useSignalR('NotificationCreated', () => {
    qc.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() })
  })

  return useQuery<number>({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: notificationsApi.unreadCount,
    staleTime: 30_000,
  })
}

/** Mark a single notification as read, with optimistic update on the list. */
export function useMarkNotificationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all })
    },
  })
}

/** Mark all notifications as read. */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all })
    },
  })
}
